import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  Badge,
  IconButton,
  Tooltip,
  Chip,
  useTheme,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import BuildIcon from '@mui/icons-material/Build';
import WarningIcon from '@mui/icons-material/Warning';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import TaskIcon from '@mui/icons-material/Assignment';
import PendingIcon from '@mui/icons-material/Pending';
import EngineeringIcon from '@mui/icons-material/Engineering';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { car as carApi, task as taskApi } from '../../../utils/api';
import { toast } from 'react-toastify';
import { TASK_STATUS, TASK_PRIORITY } from '../../../constants/task';
import { getTransformedImageUrl } from '../../../utils/filestack';

const WorkshopCarsSection = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { t } = useTranslation(['cars', 'common', 'workshop']);
  const [cars, setCars] = useState([]);
  const [groupedTasks, setGroupedTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [imageErrors, setImageErrors] = useState({});

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [carsData, tasksData] = await Promise.all([
        carApi.getAllCars(),
        taskApi.getTasksByCarGrouped()
      ]);
      
      // Create a map of task groups by car ID
      const taskGroups = (tasksData || []).reduce((acc, group) => {
        if (group.carInfo && group.carInfo._id) {
          acc[group.carInfo._id] = group;
        }
        return acc;
      }, {});
      
      // Combine cars with their task data
      const combinedData = (carsData || []).map(car => {
        const taskGroup = taskGroups[car._id];
        return {
          carInfo: car,
          _id: car._id,
          totalTasks: taskGroup?.totalTasks || 0,
          pendingTasks: taskGroup?.pendingTasks || 0,
          completedTasks: taskGroup?.completedTasks || 0,
          mostCommonStatus: taskGroup?.mostCommonStatus || TASK_STATUS.PENDING,
          tasks: taskGroup?.tasks || []
        };
      });

      // Add any cars that exist in taskGroups but not in carsData
      tasksData.forEach(group => {
        if (group.carInfo && group.carInfo._id && !combinedData.find(car => car._id === group.carInfo._id)) {
          combinedData.push({
            carInfo: group.carInfo,
            _id: group.carInfo._id,
            totalTasks: group.totalTasks || 0,
            pendingTasks: group.pendingTasks || 0,
            completedTasks: group.completedTasks || 0,
            mostCommonStatus: group.mostCommonStatus || TASK_STATUS.PENDING,
            tasks: group.tasks || []
          });
        }
      });

      // Sort cars: cars with tasks first, then by name
      const sortedData = combinedData.sort((a, b) => {
        if (a.totalTasks > 0 && b.totalTasks === 0) return -1;
        if (a.totalTasks === 0 && b.totalTasks > 0) return 1;
        return (a.carInfo?.name || '').localeCompare(b.carInfo?.name || '');
      });

      console.log('Combined and sorted car data:', sortedData);
      setCars(sortedData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error(t('errors.fetchingData'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case TASK_STATUS.PENDING:
        return <PendingIcon fontSize="small" />;
      case TASK_STATUS.IN_PROGRESS:
        return <EngineeringIcon fontSize="small" />;
      case TASK_STATUS.COMPLETED:
        return <CheckCircleIcon fontSize="small" />;
      default:
        return <TaskIcon fontSize="small" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  if (!cars.length) {
    return (
      <Box textAlign="center" py={6}>
        <DirectionsCarIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
        <Typography variant="h6" color="text.secondary">
          {t('cars.noCars')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Grid container spacing={3}>
        <AnimatePresence>
          {cars.map((car, index) => (
            <Grid item xs={6} sm={6} md={4} lg={3} key={car._id}>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.05,
                  ease: [0.4, 0, 0.2, 1]
                }}
              >
                <Paper
                  elevation={car.totalTasks > 0 ? 3 : 1}
                  sx={{
                    position: 'relative',
                    cursor: 'pointer',
                    opacity: car.totalTasks > 0 ? 1 : 0.5,
                    transition: 'all 0.3s ease',
                    overflow: 'hidden',
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: theme.shadows[6],
                      opacity: 1,
                      '& .car-image': {
                        transform: 'scale(1.1)',
                      }
                    },
                  }}
                  onClick={() => navigate(`/cars/${car._id}`)}
                  component={motion.div}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {/* Car Image Section */}
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      height: 160,
                      overflow: 'hidden',
                      bgcolor: 'action.hover'
                    }}
                  >
                    {!imageErrors[car._id] && car.carInfo?.imageUrl ? (
                      <Box
                        className="car-image"
                        component="img"
                        src={getTransformedImageUrl(car.carInfo.imageUrl, 'app')}
                        alt={car.carInfo.name}
                        onError={() => {
                          setImageErrors(prev => ({
                            ...prev,
                            [car._id]: true
                          }));
                        }}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease'
                        }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexDirection: 'column'
                        }}
                      >
                        <DirectionsCarIcon sx={{ fontSize: 60, color: 'text.secondary' }} />
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                          {car.carInfo?.model || car.carInfo?.name}
                        </Typography>
                      </Box>
                    )}
                    
                  </Box>

                  {/* Content */}
                  <Box
                    sx={{
                      p: 2,
                      flexGrow: 1,
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0 }}>
                      <Box>
                        <Typography variant="h6" component="h3">
                          {car.carInfo?.name || t('common.unknown')}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>

                  {/* Progress Bar */}
                  {car.totalTasks > 0 && (
                    <LinearProgress 
                      variant="determinate" 
                      value={car.totalTasks > 0 ? (car.completedTasks / car.totalTasks) * 100 : 0} 
                      sx={{ 
                        height: 4,
                        borderRadius: 0,
                        backgroundColor: 'rgba(0,0,0,0.1)',
                        '& .MuiLinearProgress-bar': {
                          backgroundColor: theme.palette.error.main,
                        }
                      }} 
                    />
                  )}
                </Paper>
              </motion.div>
            </Grid>
          ))}
        </AnimatePresence>
      </Grid>
    </Box>
  );
};

export default WorkshopCarsSection; 